/**
 * Distribution writes answer 202 with a `request_id`; the change is real once
 * GET /edits/{request_id} says `done`. These helpers poll until then, so a mutation hook can
 * await one promise and invalidate afterwards. A `failed` edit rejects with its own message.
 */
import { getDistributionEdit } from "./distributionsApi";
import { getTemplateEdit } from "./distributionTemplatesApi";
import { getBrandKitEdit } from "./distributionBrandKitsApi";
import type { EditStatusApi } from "./distributions.types";

export interface PollOptions {
  intervalMs?: number;
  /** Stop after this many polls; the change may still land. */
  maxPolls?: number;
}

export const EDIT_POLL_INTERVAL_MS = 400;
const MAX_POLLS = 75;

export const EDIT_TIMEOUT_MESSAGE =
  "This is taking longer than usual. Refresh the page in a minute to see if your change was saved.";

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Polls `read` until the edit is done; rejects with the edit's message when it failed. */
export const pollEdit = async <T extends Pick<EditStatusApi, "status"> & { error?: EditStatusApi["error"] }>(
  read: () => Promise<T>,
  options: PollOptions = {},
): Promise<T> => {
  const interval = options.intervalMs ?? EDIT_POLL_INTERVAL_MS;
  const maxPolls = options.maxPolls ?? MAX_POLLS;
  for (let poll = 0; poll < maxPolls; poll++) {
    const edit = await read();
    if (edit.status === "done") return edit;
    if (edit.status === "failed") {
      // Keep the failure's code (getApiErrorCode), so a caller can put e.g. a name conflict on its field.
      throw Object.assign(new Error(edit.error?.message || "The change couldn’t be saved. Try again."), { code: edit.error?.code });
    }
    await sleep(interval);
  }
  throw new Error(EDIT_TIMEOUT_MESSAGE);
};

export const waitForDistributionEdit = (storyId: string, requestId: string, options?: PollOptions): Promise<EditStatusApi> =>
  pollEdit(() => getDistributionEdit(storyId, requestId), options);

export const waitForTemplateEdit = (requestId: string, options?: PollOptions): Promise<EditStatusApi> =>
  pollEdit(() => getTemplateEdit(requestId), options);

// ─── Round 6 · brand kits (WP7) ───
export const waitForBrandKitEdit = (requestId: string, options?: PollOptions): Promise<EditStatusApi> =>
  pollEdit(() => getBrandKitEdit(requestId), options);
